import React, { useRef, useState } from "react";
import {  Box, Grid, IconButton, TextField, Typography } from "@mui/material";
import useStyles from '../../../../styles/global-mui.js';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import '../match/matchview.css'
import { useDispatch } from "react-redux";
import { MatchEventFactory } from "../match/data/factories/MatchEventFactory.js";
import GameEventButtons from "../match/gameevents/GameEventButtons.js";
import PostsManager from "../match/posts/PostsManager.js";
import ActuPreview from "./preview/ActuPreview.js";
import { MEDIA_ARTICLE, MEDIA_NOTATION, MEDIA_QUESTIONBOX, MEDIA_QUIZZ, MEDIA_SURVEY } from "../match/data/constants.js";
import { deleteNotation } from "../../../../actions/notation.js";
import { deleteArticle } from "../../../../actions/article.js";
import { deleteSurvey } from "../../../../actions/sondage.js";
import { deleteQuizz } from "../../../../actions/quizz.js";
import { DirectProvider } from "../match/DirectContext.js";
import TagsManager from "./tags/TagsManager.js";
import { updateActuLive } from "../../../../actions/actulives.js";
import { deleteQuestionBox } from "../../../../actions/questionBox.js";
import DsasButton from "../../../components/DsasButton/DsasButton.js";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { confirmActionNotification } from "../../../../actions/notifications.js";


// --- Helpers "draft" + reconcile ---------------------------------------------

// UUID sans dépendance (fallback si crypto.randomUUID indispo)
const genId = () => (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? `tmp_${crypto.randomUUID()}`
  : `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// Fusionne ce que renvoie le serveur (serverPosts) avec les posts locaux
// -> garde tous les brouillons locaux (isDraft === true ou id "tmp_*")
// -> pour les posts "persistés", la version serveur prévaut
function reconcilePosts(localPosts, serverPosts) {
  const serverById = new Map(serverPosts.map(p => [String(p.id), p]));
  const merged = [...serverPosts];

  // réinjecte les brouillons locaux absents du serveur
  for (const lp of localPosts) {
    const isDraft = lp.isDraft || String(lp.id).startsWith('tmp_');
    if (isDraft && !serverById.has(String(lp.id))) {
      merged.push(lp);
    }
  }
  return merged;
}

// Mapping atomique tmp_id -> real_id, en conservant l'actif si nécessaire
function mapTmpToRealId(prevPosts, { tmpId, realId, realPostData }, activePost) {
  return {
    posts: prevPosts.map(p => (p.id === tmpId ? { ...p, ...realPostData, id: realId, tmpId:tmpId, isDraft: false } : p)),
    newActive: activePost === tmpId ? realId : activePost,
  };
}

export const ActuView = ({ actu, loadMore }) => {

  const dispatch = useDispatch()

  const postsManagerRef = useRef(null); // to set anchor & go up in gamefact edit mode (mobile device view)
  const gamefactsButtonsRef = useRef(null); // to set anchor & go up in gamefact edit mode (mobile device view)

  const [posts, setPosts] = useState([]);
  const [activePost, setActivePost] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(actu.name);

  const setPostsFromServer = (serverPosts) => {
    setPosts(prev => reconcilePosts(prev, serverPosts));
    // <- garde les brouillons et évite les fermetures "fantômes"
  };

  const upgradeTmpPostId = ({ tmpId, realId, realPostData }) => {
    setPosts(prev => {
      // console.log('Upgrading tmpId', tmpId, 'to realId', realId, 'with data', realPostData);
     
      const { posts: nextPosts, newActive } = mapTmpToRealId(prev, { tmpId, realId, realPostData }, activePost);
      //  console.log('newActive:', newActive);
      //  console.log('activePost before upgrade:', activePost);
      if (newActive !== activePost) setActivePost(newActive);
      // console.log('Posts after upgrade:', nextPosts); 
      return nextPosts;
    });
  };

  const handleEventClick = (event, showInPopup = false, callback) => {

    scrollToTabs();

    setPosts((prevPosts) => {
        const eventDto = MatchEventFactory.create(event) 

        // Définir timer et period grace au match
        eventDto.initTimerPeriod(actu);

        const newPost = {
            id: genId(),           // id brouillon stable et unique
            isDraft: true,         // flag explicite
            event: eventDto,
            showInPopup,
            onValidate: callback,
          };

        const updatedPosts = [...prevPosts, newPost];
        // console.log('HandleEventClick Set active post ', newPost);
        setActivePost(newPost.id);
        return updatedPosts; 
      });
  };

   const scrollToTabs = () => {
    
    if (postsManagerRef.current) {
      postsManagerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

  }

  const scrollToGamefactsButtons = () => {
    
    if (gamefactsButtonsRef.current) {
      gamefactsButtonsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

  }

  const handleEventEdit = (event) => {

    scrollToTabs();

    setPosts((prevPosts) => {
      const newPost = {
          id: genId(),           // id brouillon stable et unique
          isDraft: true,         // flag explicite
          //title: event.title ?? event._id, 
          event : MatchEventFactory.create(event) 
      };
      const updatedPosts = [...prevPosts, newPost];
  
      setActivePost(newPost.id);
      return updatedPosts; 
    });
  }

  const handleViewObserver = (item) => {

    //TODO add to setPost directly everywhere?
    scrollToTabs();

      // L'idee ici est d'ouvrir un post qui permet d'observer les questions
      setPosts((prevPosts) => {
        const newPost = {
            id: genId(),           // id brouillon stable et unique
            isDraft: true,         // flag explicite
            //title: event.title ?? event._id, 
            observer : item,
            action : (pseudo, question) => {
              const event = {
                tab: "Réponse à "+pseudo,
                subscription : 1,
                type : MEDIA_ARTICLE,
                content : `<p>${question} ? <strong>${pseudo}</strong> </p><p><br></p><p><br></p><p><br></p>`
              }
              handleEventClick(event)
            }
        };
  
        const updatedPosts = [...prevPosts, newPost];
  
        setActivePost(newPost.id);
        return updatedPosts;
      });
      
    }

  const handleEventDelete = (event) => {

    dispatch(confirmActionNotification("Êtes-vous sûr de vouloir supprimer ce post ?", () => {

    switch(event.type){
        case MEDIA_NOTATION :
          dispatch(deleteNotation(event._id))
        break
        case MEDIA_ARTICLE :
          dispatch(deleteArticle(event._id))
        break
        case MEDIA_SURVEY :
          dispatch(deleteSurvey(event._id))
        break
        case MEDIA_QUIZZ :
          dispatch(deleteQuizz(event._id))
        break
        case MEDIA_QUESTIONBOX :
          dispatch(deleteQuestionBox(event._id))
        break
        default :
          console.warn('Event type : '+ event.type + ' inconnu.')
      }
    }));
  }

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
      dispatch(updateActuLive(actu.idActu, {name : editedName}))
      // Fin de l'édition
      setIsEditing(false);
  };

  const handleCancel = () => {
    // Annule l'édition et réinitialise le nom édité
    setIsEditing(false);
    setEditedName(actu.name);
  };

  const handleChange = (e) => {
    // Met à jour le nom édité en fonction de la saisie utilisateur
    setEditedName(e.target.value);
  };

  const isValidUrlName = (value) => {
  // Autorise lettres (avec accents), chiffres, espace, tiret, underscore, point
  const regex = /^[\p{L}\p{N}._\- ]+$/u;
  return regex.test(value);
};

  const classes = useStyles();

  return (
    <DirectProvider directEvent={actu}>
      <Grid container>
        <Grid item xs={12} sm={12} md={8} lg={9} >
                <Box sx={{display : 'flex', justifyContent : 'center'}}>
                    {isEditing ? (
                    <Box sx={{display : 'flex', alignItems : 'center', flexDirection: 'column'}}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TextField
                          className={classes.customInput}
                          sx={{ width: { xs : '300px', sm: '300px', md: '400px', lg: '600px' } }}
                          label="Titre de l'actualité"
                          type="text"
                          value={editedName}
                          onChange={handleChange}
                          error={!!editedName && !isValidUrlName(editedName)}
                          helperText={
                            !!editedName && !isValidUrlName(editedName)
                              ? "Caractères non valides (lettres, chiffres, espaces, tirets, . et _ uniquement)"
                              : ''
                          }
                          multiline
                          minRows={1}
                          maxRows={3}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault(); // empêche saut de ligne
                            }
                          }}
                        />
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (isValidUrlName(editedName)) {
                                handleSave();
                              }
                            }}
                            disabled={!isValidUrlName(editedName)}
                          >
                            <DoneIcon />
                          </IconButton>
                          <IconButton size={"small"} onClick={handleCancel}>
                          <CloseIcon />
                          </IconButton>
                        </Box>
                    </Box>
                    ) : (
                    <Box className='centered-medium-wrapper' sx={{flexDirection: 'column'}}>
                          <Typography className={classes.customSubtitle} sx={{
                            textTransform:'none',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            }}>{actu.name}</Typography>
                          <DsasButton icon={faEdit} label="Editer" variant = 'secondary' onClick={handleEdit} customCss={{margin:0}} />
                    </Box>
                    )}


                    
                </Box>

          <TagsManager actu={actu}/>

          
          {/* SCROLL ANCHOR GFs*/}
          <div ref={gamefactsButtonsRef} className="live-gf-scroll-target"/>
          <GameEventButtons discipline={actu.discipline} onEventClick={handleEventClick} />
          {/* SCROLL ANCHOR POSTS*/}
          <div ref={postsManagerRef} className="live-gf-scroll-target"/>
          <PostsManager 
            posts={posts} 
            setPosts={setPosts} 
            activePost={activePost} 
            setActivePost={setActivePost} 
            submitCallback={scrollToGamefactsButtons}
            upgradeTmpPostId={upgradeTmpPostId}
          />

        </Grid>
         {/*LIVE PREVIEW / HISTORY */}
         <Grid item xs={12} sm={12} md={4} lg={3} >
          <Box className='bordered-wrapper small-padding' sx={{minHeight : '60rem'}}>
            <Box className='centered-wrapper'>
              <Typography variant='h2' sx={{textTransform:'uppercase'}}>Prévisualisation</Typography>
            </Box>
            <Box className='small-padding'></Box>
            <Box className='large-separator'></Box>
            <ActuPreview loadMore={loadMore} handleEventEdit={handleEventEdit} handleEventDelete={handleEventDelete} handleViewObserver={handleViewObserver}/>
          </Box>
        </Grid>
      </Grid>
    </DirectProvider>
  );
};